import { apiConfig } from "@/config/api";
import { getAuthHeaders } from "@/utils/authUtils";
import { toast } from "@/components/ui/sonner";

const SUBSCRIPTION_TIERS = {
  'free': { limit: 2 },
  'basic': { limit: 2 },
  'standard': { limit: 10 },
  'premium': { limit: 25 },
  'free_premium': { limit: 25 }
};

export const alertCleanupService = {
  async enforceAlertLimit(currentTier: 'free' | 'basic' | 'standard' | 'premium' | 'free_premium', previousTier?: 'free' | 'basic' | 'standard' | 'premium' | 'free_premium') {
    console.log(`🧹 [ALERT_CLEANUP] Enforcing alert limit for tier: ${currentTier}`);
    
    try {
      const currentLimit = SUBSCRIPTION_TIERS[currentTier].limit;
      const previousLimit = previousTier ? SUBSCRIPTION_TIERS[previousTier].limit : null;
      
      // Only do cleanup if we're moving to a lower tier
      if (previousLimit && currentLimit >= previousLimit) {
        console.log(`📈 [ALERT_CLEANUP] No cleanup needed - not a downgrade (${previousLimit} -> ${currentLimit})`);
        return;
      }

      // Fetch current alerts
      const alertsResponse = await fetch(`${apiConfig.baseUrl}/api/alerts`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!alertsResponse.ok) {
        console.error('❌ [ALERT_CLEANUP] Failed to fetch alerts for cleanup');
        return;
      }

      const alerts = await alertsResponse.json();
      const activeAlerts = alerts.filter((alert: any) => alert.is_active === true);
      
      console.log(`📊 [ALERT_CLEANUP] Found ${activeAlerts.length} active alerts, limit is ${currentLimit}`);

      if (activeAlerts.length > currentLimit) {
        const excessCount = activeAlerts.length - currentLimit;
        console.log(`🗑️ [ALERT_CLEANUP] Need to remove ${excessCount} alerts`);

        // Sort by creation date (newest first) and take the excess alerts
        const sortedAlerts = activeAlerts.sort((a: any, b: any) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        const alertsToRemove = sortedAlerts.slice(0, excessCount);
        console.log(`🗑️ [ALERT_CLEANUP] Removing ${alertsToRemove.length} newest alerts:`, alertsToRemove.map((a: any) => a.search_term));

        // Delete the excess alerts
        for (const alert of alertsToRemove) {
          try {
            const deleteResponse = await fetch(`${apiConfig.baseUrl}/api/alerts/${alert.search_id}`, {
              method: 'DELETE',
              headers: getAuthHeaders()
            });

            if (!deleteResponse.ok) {
              console.error(`❌ [ALERT_CLEANUP] Failed to delete alert ${alert.search_id}`);
            } else {
              console.log(`✅ [ALERT_CLEANUP] Deleted alert: ${alert.search_term}`);
            }
          } catch (error) {
            console.error(`🔥 [ALERT_CLEANUP] Error deleting alert ${alert.search_id}:`, error);
          }
        }

        toast.info(`Removed ${excessCount} newest alert${excessCount === 1 ? '' : 's'} to match your ${currentTier} plan limit.`);
        
        // Dispatch a custom event to notify components that alerts were cleaned up
        window.dispatchEvent(new CustomEvent('alertsCleanedUp', { detail: { removedCount: excessCount } }));
        
        return excessCount;
      } else {
        console.log(`✅ [ALERT_CLEANUP] No cleanup needed - ${activeAlerts.length} alerts within ${currentLimit} limit`);
        return 0;
      }
    } catch (error) {
      console.error('🔥 [ALERT_CLEANUP] Error during alert cleanup:', error);
      return 0;
    }
  }
};